import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Icon from '../components/Icon';
import PermissionsEditor from '../components/PermissionsEditor';

const TEMPLATE_LABELS = {
  main: 'Ana Onboarding Maili',
  manay: 'Manay CPA Tanışma',
  sky: 'Sky Accounting Tanışma',
  incentive: 'Teşvik Maili',
};

export default function Settings() {
  const { user, can } = useAuth();
  const [tab, setTab] = useState('overview');
  const isAdmin = user?.role === 'admin';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Ayarlar</h1>
          <p className="page-subtitle">Sistem ve mail şablonu ayarları</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12,
          background: '#D4FF3A',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, fontWeight: 700, color: '#0E0F11', flexShrink: 0
        }}>
          {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#F2EFE6' }}>{user?.name || 'Kullanıcı'}</div>
          <div style={{ fontSize: 14, color: '#9A9A92' }}>{user?.email}</div>
          <span className="badge" style={{ background: '#D4FF3A22', color: '#D4FF3A', marginTop: 4, display: 'inline-block' }}>
            {user?.role || 'viewer'}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #17191C', marginBottom: 24 }}>
        {[
          { key: 'overview', label: 'Genel' },
          { key: 'profile', label: 'Profil' },
          { key: 'templates', label: 'Mail Şablonları' },
          ...(isAdmin ? [{ key: 'users', label: 'Kullanıcılar' }] : []),
          ...(can('settings.templates') ? [{ key: 'slack', label: 'Slack Kanalları' }] : []),
          ...(isAdmin ? [{ key: 'permissions', label: 'Yetkilendirme' }] : []),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'none', border: 'none',
              color: tab === t.key ? '#D4FF3A' : '#9A9A92',
              padding: '10px 16px', cursor: 'pointer',
              fontWeight: tab === t.key ? 600 : 400,
              borderBottom: tab === t.key ? '2px solid #D4FF3A' : '2px solid transparent',
              fontSize: 14,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewSection onOpen={setTab} isAdmin={isAdmin} canTemplates={can('settings.templates')} />}
      {tab === 'profile' && <ProfileSection />}
      {tab === 'templates' && <TemplatesEditor canEdit={can('settings.templates')} />}
      {tab === 'users' && isAdmin && <UsersManager currentUserId={user.id} />}
      {tab === 'slack' && can('settings.templates') && <SlackTemplatesEditor />}
      {tab === 'permissions' && isAdmin && <PermissionsEditor />}
    </div>
  );
}

const ROLE_LABELS = {
  admin: 'Admin',
  finance: 'Finans',
  operations: 'Operasyon',
  sales: 'Satış',
  account_manager: 'Account Manager',
  ppc_manager: 'PPC Manager',
  viewer: 'Görüntüleyici',
};

// Kullanıcı yönetimi (sadece admin): ekle, rol değiştir, pasifleştir.
// Açık kayıt kapalı olduğu için yeni ekip üyeleri buradan eklenir.
// Davet / sıfırlama sonucu: mail gitti mi, gitmediyse neden; link her durumda kopyalanabilir
function LinkResult({ r, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(r.link); setCopied(true); } catch (e) { /* elle seçilebilir */ } };
  const until = r.expires_at ? new Date(r.expires_at).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: r.emailed ? 'var(--green)' : 'var(--yellow)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 14 }}>
          {r.emailed
            ? <><strong>{r.who}</strong> adresine {r.kind === 'invite' ? 'davet' : 'şifre sıfırlama'} maili gönderildi.</>
            : <><strong>{r.who}</strong> için mail gönderilmedi: {r.reason}</>}
          <div className="text-muted text-sm" style={{ marginTop: 4 }}>Bağlantı tek kullanımlık{until ? `, ${until} tarihine kadar geçerli` : ''}. Gerekirse aşağıdan kopyalayıp kendiniz iletebilirsiniz.</div>
        </div>
        <button className="btn btn--secondary" style={{ alignSelf: 'flex-start', padding: '2px 10px' }} onClick={onClose}>Kapat</button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input className="form-input" readOnly value={r.link} onFocus={e => e.target.select()} style={{ fontFamily: 'monospace', fontSize: 12 }} />
        <button className="btn btn--secondary" onClick={copy} style={{ whiteSpace: 'nowrap' }}>{copied ? 'Kopyalandı' : 'Kopyala'}</button>
      </div>
    </div>
  );
}

function UsersManager({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', role: 'account_manager' });
  const [linkResult, setLinkResult] = useState(null); // { who, kind, link, emailed, reason, expires_at }
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/users?all=1').then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const r = await api.post('/users', form, { silent: true });
      if (r.data.invite) setLinkResult({ who: r.data.user.email, kind: 'invite', ...r.data.invite });
      setForm({ name: '', email: '', role: 'account_manager' });
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendLink = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/send-link`);
      setLinkResult({ who: u.email, ...r.data });
      load();
    } catch (err) { /* hata api katmanında gösterilir */ }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/users/${id}`, patch);
      load();
    } catch (err) {
      // hata api katmanında gösterilir
    }
  };

  const inputStyle = { padding: '8px 10px', background: '#17191C', border: '1px solid #2A2D32', borderRadius: 6, color: '#F2EFE6', fontSize: 14 };

  return (
    <div>
      <form onSubmit={create} className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <input style={inputStyle} placeholder="Ad Soyad" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        <input style={inputStyle} placeholder="E-posta" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
        <select style={inputStyle} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
          {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button className="btn btn--primary" type="submit" disabled={saving}>{saving ? '…' : '+ Davet Et'}</button>
        <span className="text-muted text-sm" style={{ flexBasis: '100%' }}>
          Şifre girmenize gerek yok: kişiye kendi şifresini belirleyeceği bir bağlantı gider (sadece @boldranks.com adreslerine mail atılır, 72 saat geçerli).
        </span>
        {error && <span style={{ color: '#FF6B5B', fontSize: 13 }}>{error}</span>}
      </form>

      {linkResult && <LinkResult r={linkResult} onClose={() => setLinkResult(null)} />}

      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="data-table">
          <thead>
            <tr><th>Ad</th><th>E-posta</th><th>Rol</th><th title="Slack davetleri bu ID ile yapılır">Slack ID</th><th title="Katıldığı toplantıların Fireflies dökümü gönderilsin">Döküm</th><th>Giriş</th><th>Durum</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                <td style={{ fontWeight: 600 }}>{u.name}</td>
                <td>
                  <EmailInput value={u.email} onSave={(v) => update(u.id, { email: v })} style={inputStyle} />
                </td>
                <td>
                  <select style={inputStyle} value={u.role} disabled={u.id === currentUserId}
                    onChange={e => update(u.id, { role: e.target.value })}>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td>
                  <SlackIdInput value={u.slack_user_id} onSave={(v) => update(u.id, { slack_user_id: v })} style={inputStyle} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={!!u.wants_transcripts}
                    onChange={e => update(u.id, { wants_transcripts: e.target.checked })} />
                </td>
                <td>
                  {u.invite_pending && <span className="badge" style={{ background: 'var(--accent-soft)', color: 'var(--accent)', marginRight: 6 }}>Davet bekliyor</span>}
                  {u.id !== currentUserId && u.is_active && (
                    <button className="btn btn--secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => sendLink(u)}
                      title={u.invite_pending ? 'Yeni davet bağlantısı oluştur ve gönder' : 'Şifre sıfırlama bağlantısı oluştur ve gönder'}>
                      {u.invite_pending ? 'Daveti tekrar gönder' : 'Şifre sıfırlama linki'}
                    </button>
                  )}
                </td>
                <td>
                  {u.id === currentUserId ? <span className="text-muted">Siz</span> : (
                    <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={() => update(u.id, { is_active: !u.is_active })}>
                      {u.is_active ? 'Pasifleştir' : 'Aktifleştir'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
        Rol değişikliği, kullanıcının bir sonraki girişinde geçerli olur. Slack ID: Slack'te profil → ⋮ → "Copy member ID" (U… ile başlar).
        Slack ID'si olan aktif kullanıcılar yeni hesap kanallarına otomatik davet edilir.
      </p>
    </div>
  );
}

// E-posta: toplantı eşleştirmesi için takvim/Fireflies'ta kullanılan @boldranks.com iş adresi olmalı
function EmailInput({ value, onSave, style }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const dirty = val.trim().toLowerCase() !== (value || '').toLowerCase();
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <input style={{ ...style, width: 210 }} type="email" value={val} onChange={e => setVal(e.target.value)}
        title="Toplantılarda kullanılan iş adresi (ör. ad@boldranks.com)" />
      {dirty && <button className="btn" style={{ fontSize: 12, padding: '4px 8px' }}
        onClick={() => { if (window.confirm(`E-posta ${value} → ${val.trim()} olarak değiştirilsin mi? Kişi bundan sonra bu adresle giriş yapar.`)) onSave(val.trim()); }}>Kaydet</button>}
    </div>
  );
}

function SlackIdInput({ value, onSave, style }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const dirty = val.trim() !== (value || '');
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <input style={{ ...style, width: 120 }} placeholder="U0…" value={val} onChange={e => setVal(e.target.value.toUpperCase())} />
      {dirty && <button className="btn" style={{ fontSize: 12, padding: '4px 8px' }} onClick={() => onSave(val.trim())}>Kaydet</button>}
    </div>
  );
}

function OverviewSection({ onOpen, isAdmin, canTemplates }) {
  const cards = [
    { key: 'profile', title: 'Profil', desc: 'Şifrenizi değiştirin', icon: 'user' },
    { key: 'templates', title: 'Mail Şablonları', desc: 'Onboarding mail metinleri', icon: 'mail' },
    ...(isAdmin ? [{ key: 'users', title: 'Ekip Yönetimi', desc: 'Kullanıcı davet et, rol ver, şifre sıfırlama linki gönder', icon: 'users' }] : []),
    ...(canTemplates ? [{ key: 'slack', title: 'Slack Kanalları', desc: 'Yeni markada açılacak kanallar ve kanallara otomatik eklenecek kişiler', icon: 'inbox' }] : []),
    ...(isAdmin ? [{ key: 'permissions', title: 'Yetkilendirme', desc: 'Hangi rol neyi görüp yapabilir: faturalandırma, hesaplar, toplantılar, bildirimler', icon: 'lock' }] : []),
  ];
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {cards.map(c => (
        <div key={c.key} className="card card--hoverable" style={{ flex: '1 1 240px', cursor: 'pointer' }} onClick={() => onOpen(c.key)}>
          <div style={{ marginBottom: 12, color: 'var(--accent)' }}><Icon name={c.icon} size={26} /></div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{c.title}</div>
          <div className="text-muted text-sm">{c.desc}</div>
        </div>
      ))}
    </div>
  );
}

// Kendi şifresini değiştirme; diğer cihazlardaki oturumlar kapanır
function ProfileSection() {
  const [f, setF] = useState({ current: '', next: '', again: '' });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return setMsg({ err: true, text: 'Yeni şifre en az 8 karakter olmalı' });
    if (f.next !== f.again) return setMsg({ err: true, text: 'Yeni şifreler aynı değil' });
    setSaving(true); setMsg(null);
    try {
      const r = await api.post('/auth/change-password', { current_password: f.current, new_password: f.next }, { silent: true });
      localStorage.setItem('token', r.data.token);
      setF({ current: '', next: '', again: '' });
      setMsg({ err: false, text: 'Şifreniz değiştirildi. Diğer cihazlardaki oturumlar kapatıldı.' });
    } catch (err) {
      setMsg({ err: true, text: err.response?.data?.message || 'Şifre değiştirilemedi' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="card" style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h3 className="card__title" style={{ margin: 0 }}>Şifre değiştir</h3>
      <input className="form-input" type="password" autoComplete="current-password" placeholder="Mevcut şifre" value={f.current} onChange={e => setF({ ...f, current: e.target.value })} required />
      <input className="form-input" type="password" autoComplete="new-password" placeholder="Yeni şifre (en az 8 karakter)" value={f.next} onChange={e => setF({ ...f, next: e.target.value })} required />
      <input className="form-input" type="password" autoComplete="new-password" placeholder="Yeni şifre (tekrar)" value={f.again} onChange={e => setF({ ...f, again: e.target.value })} required />
      {msg && <div style={{ fontSize: 13, color: msg.err ? 'var(--red)' : 'var(--green)' }}>{msg.text}</div>}
      <button className="btn btn--primary" type="submit" disabled={saving}>{saving ? 'Kaydediliyor…' : 'Şifreyi değiştir'}</button>
    </form>
  );
}

function TemplatesEditor({ canEdit }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get('/email-templates');
      setTemplates(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (selected) {
      setForm({ subject: selected.subject || '', body: selected.body || '', is_active: selected.is_active });
    }
  }, [selected]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/email-templates/${selected.id}`, form);
      await fetchAll();
      const refreshed = (await api.get('/email-templates')).data.find(t => t.id === selected.id);
      setSelected(refreshed);
    } catch (err) {
      alert('Kaydedilemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const seedDefaults = async () => {
    if (!window.confirm('Varsayılan TR şablonları DB\'ye yüklensin mi? (Mevcut olanlar atlanır)')) return;
    try {
      const res = await api.post('/email-templates/seed');
      alert(`${res.data.inserted} şablon eklendi, ${res.data.skipped} mevcut atlandı.`);
      fetchAll();
    } catch (err) {
      alert('Seed başarısız: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <div className="page-loading">Yükleniyor...</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
      {/* Sol — şablon listesi */}
      <div>
{canEdit && (
                <button className="btn btn--secondary" style={{ width: '100%', marginBottom: 12 }} onClick={seedDefaults}>
          <Icon name="bolt" size={14} /> Varsayılanları Yükle
        </button>
        )}
        {templates.length === 0 && (
          <div className="text-muted" style={{ padding: 16, textAlign: 'center', fontSize: 13 }}>
            Henüz şablon yok. Yukarıdaki butonla varsayılanları yükle.
          </div>
        )}
        {templates.map(t => (
          <div
            key={t.id}
            className="card"
            style={{
              padding: 12, marginBottom: 8, cursor: 'pointer',
              border: selected?.id === t.id ? '1px solid #D4FF3A' : '1px solid #17191C',
            }}
            onClick={() => setSelected(t)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ color: '#F2EFE6', fontSize: 13 }}>
                {TEMPLATE_LABELS[t.template_key] || t.template_key}
              </strong>
              <span className="badge" style={{ background: '#D4FF3A22', color: '#D4FF3A', fontSize: 11 }}>
                {t.language}
              </span>
            </div>
            {!t.is_active && (
              <span style={{ fontSize: 11, color: '#FF6B5B' }}>Pasif</span>
            )}
          </div>
        ))}
      </div>

      {/* Sağ — düzenleyici */}
      <div>
        {!selected ? (
          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
            <div className="text-muted">Düzenlemek için sol taraftan bir şablon seç</div>
          </div>
        ) : (
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ color: '#F2EFE6', margin: 0 }}>
                {TEMPLATE_LABELS[selected.template_key] || selected.template_key} · {selected.language}
              </h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#C9C7BE', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
                Aktif
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Konu</label>
              <input className="form-input" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
            </div>

            <div className="form-group">
              <label className="form-label">İçerik</label>
              <textarea
                className="form-input"
                style={{ minHeight: 400, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 }}
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
              />
            </div>

            <div style={{ background: '#0A0B0D', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#9A9A92' }}>
              <strong style={{ color: '#C9C7BE' }}>Placeholder'lar:</strong><br/>
              <code>{`{{first_name}}`}</code> · <code>{`{{company_name}}`}</code> · <code>{`{{brand_name}}`}</code> · <code>{`{{subject_topic}}`}</code> · <code>{`{{sellerboard_email}}`}</code><br/>
              <strong style={{ color: '#C9C7BE', marginTop: 8, display: 'block' }}>Sadece ana mailde — koşullu bölümler:</strong>
              <code>{`<!-- if:company_setup --> ... <!-- endif:company_setup -->`}</code><br/>
              <code>{`<!-- if:mercury_setup --> ... <!-- endif:mercury_setup -->`}</code><br/>
              <code>{`<!-- if:incentive --> ... <!-- endif:incentive -->`}</code><br/>
              Sıralı numaralandırma için: <code>{`{{N}}`}</code> (otomatik 1, 2, 3...)
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              {canEdit ? (
                <button className="btn btn--primary" style={{ flex: 1 }} onClick={save} disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Şablonu Kaydet'}
                </button>
              ) : (
                <span className="text-muted text-sm" style={{ flex: 1, alignSelf: 'center' }}>Şablonları sadece admin değiştirebilir.</span>
              )}
              <button className="btn btn--secondary" onClick={() => setSelected(null)}>İptal</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Yeni markada açılacak Slack kanalları: ek, ad, atananlar (kanal bazında) ve sabit üyeler
function SlackTemplatesEditor() {
  const [list, setList] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  React.useEffect(() => {
    api.get('/slack-channel-templates?all=1').then(r => setList(r.data)).catch(() => setList([]));
    api.get('/users').then(r => setUsers(r.data.filter(u => u.is_active !== false))).catch(() => {});
  }, []);
  if (!list) return <div className="text-muted">Yükleniyor…</div>;
  const set = (i, patch) => { setMsg(''); setList(l => l.map((t, j) => (j === i ? { ...t, ...patch } : t))); };
  const slug = (x) => String(x || '').toLocaleLowerCase('tr').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ç/g, 'c')
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await api.put('/slack-channel-templates', { templates: list });
      setList(r.data); setMsg('Kaydedildi. Bundan sonra açılan markalarda geçerli.');
    } catch (e) { setMsg(e.response?.data?.error || 'Kaydedilemedi'); }
    setSaving(false);
  };
  const byId = Object.fromEntries(users.map(u => [u.id, u]));
  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Yeni marka Slack kanalları</h3>
      <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
        Yeni hesap açılırken bu kanallar önerilir; kanal adı = marka adı + ek (ör. Maxel Ju → #maxel_ju_internal).
        Formda seçilen AM / PPC / Satışçı, işaretli kanallara otomatik eklenir. Sabit üyeler her yeni markada o kanala eklenir.
        Formu dolduran kişi her kanalın üyelerini yine elle değiştirebilir.
      </p>
      <div className="sc-card" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>"Tüm ekip" kapsamı</strong>
          <span className="text-muted"> — Slack ID'si olan aktif kullanıcılar. Kapatılan kişi Slack ID'sini korur ama yeni marka kanallarına otomatik eklenmez (formdan elle eklenebilir).</span>
        </div>
        <div className="sc-members">
          {users.filter(u => u.slack_user_id).map(u => {
            const on = u.slack_auto_invite !== false;
            return (
              <button key={u.id} type="button" className={`sc-chip ${on ? '' : 'is-off'}`} title={on ? 'Tüm ekip\'e dahil — çıkarmak için tıkla' : 'Tüm ekip\'e dahil değil — eklemek için tıkla'}
                style={{ cursor: 'pointer', paddingRight: 10 }}
                onClick={async () => {
                  try {
                    const r = await api.patch(`/users/${u.id}`, { slack_auto_invite: !on });
                    setUsers(us => us.map(x => (x.id === u.id ? { ...x, slack_auto_invite: r.data.slack_auto_invite } : x)));
                  } catch (e) { setMsg(e.response?.data?.error || 'Güncellenemedi'); }
                }}>
                <Icon name={on ? 'check' : 'x'} size={11} /> {u.name}
              </button>
            );
          })}
          {users.filter(u => !u.slack_user_id).length > 0 && (
            <span className="text-muted" style={{ fontSize: 12 }}>
              Slack ID'si olmayanlar: {users.filter(u => !u.slack_user_id).map(u => u.name).join(', ')}
            </span>
          )}
        </div>
      </div>
      {list.map((t, i) => (
        <div key={i} className="sc-card" style={{ marginBottom: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 10, alignItems: 'center' }}>
            <div>
              <div className="sc-name">#marka{t.suffix ? `_${slug(t.suffix)}` : ''}</div>
              <input className="form-input" style={{ padding: '5px 8px', marginTop: 4 }} placeholder="ek (boş = ana kanal)" value={(t.suffix || '').replace(/^_/, '')}
                onChange={(e) => set(i, { suffix: e.target.value })} />
            </div>
            <input className="form-input" placeholder="Açıklama" value={t.label} onChange={(e) => set(i, { label: e.target.value })} />
            <button type="button" className="btn btn--secondary" style={{ padding: '4px 10px' }} title="Bu kanalı kaldır"
              onClick={() => { if (window.confirm('Bu kanal yeni markalarda artık açılmayacak. Emin misiniz?')) setList(l => l.filter((_, j) => j !== i)); }}>Kaldır</button>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '10px 0', fontSize: 13 }}>
            <span className="text-muted">Otomatik eklenecekler:</span>
            {[['include_all', 'Tüm ekip (Slack ID\'si olanlar)'], ['include_am', 'Account Manager'], ['include_ppc', 'PPC Manager'], ['include_sales', 'Satışçı']].map(([k, l]) => (
              <label key={k} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!t[k]} onChange={(e) => set(i, { [k]: e.target.checked })} /> {l}
              </label>
            ))}
            <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer', marginLeft: 'auto' }} title="CRM'deki 'Slack kanalı' bu kanalı gösterir; müşteri Slack Connect ile davet edilir">
              <input type="checkbox" checked={!!t.is_client} onChange={(e) => setList(l => l.map((x, j) => ({ ...x, is_client: j === i ? e.target.checked : (e.target.checked ? false : x.is_client) })))} /> Müşteriyle ortak kanal
            </label>
          </div>
          <div className="sc-members">
            <span className="text-muted" style={{ fontSize: 13 }}>Sabit üyeler:</span>
            {(t.fixed_user_ids || []).map(id => byId[id] && (
              <span key={id} className={`sc-chip ${byId[id].slack_user_id ? '' : 'is-missing'}`} title={byId[id].slack_user_id ? '' : 'Slack ID eksik'}>
                {byId[id].name}
                <button type="button" onClick={() => set(i, { fixed_user_ids: t.fixed_user_ids.filter(x => x !== id) })} aria-label="Çıkar">×</button>
              </span>
            ))}
            <select className="form-input" style={{ width: 'auto', padding: '3px 8px', fontSize: 12.5 }} value=""
              onChange={(e) => e.target.value && set(i, { fixed_user_ids: [...(t.fixed_user_ids || []), Number(e.target.value)] })}>
              <option value="">+ Kişi ekle</option>
              {users.filter(u => !(t.fixed_user_ids || []).includes(u.id)).map(u => <option key={u.id} value={u.id}>{u.name}{u.slack_user_id ? '' : ' (Slack ID yok)'}</option>)}
            </select>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
        <button type="button" className="btn btn--secondary" onClick={() => setList(l => [...l, { suffix: '', label: '', include_all: false, include_am: true, include_ppc: false, include_sales: false, fixed_user_ids: [], is_client: false }])}>+ Kanal ekle</button>
        <button type="button" className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
        {msg && <span className="text-muted" style={{ fontSize: 13 }}>{msg}</span>}
      </div>
    </div>
  );
}
